'use client';

import { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import StarNetwork from './StarNetwork';

type AuthMode = 'sso' | 'dev' | 'loading';

function LoginForm() {
  const t = useTranslations('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [devToken, setDevToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('loading');
  const [showDevForm, setShowDevForm] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';

  useEffect(() => {
    fetch(`${basePath}/api/auth/status`)
      .then((r) => r.json())
      .then((d) => {
        if (d.ssoEnabled) setAuthMode('sso');
        else if (d.devEnabled) setAuthMode('dev');
        else setAuthMode('sso');
      })
      .catch(() => setAuthMode('sso'));
  }, [basePath]);

  const handleSSOLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const resp = await fetch(`${basePath}/api/auth/login`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await resp.json();
      if (data.success) {
        const redirect = searchParams.get('redirect') || '/meetings';
        router.push(redirect);
      } else {
        setError(data.message || t('loginFailed'));
      }
    } catch {
      setError(t('networkError'));
    }
    setLoading(false);
  };

  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const resp = await fetch(`${basePath}/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token: devToken }),
      });

      const data = await resp.json();
      if (data.success) {
        const redirect = searchParams.get('redirect') || '/meetings';
        router.push(redirect);
      } else {
        setError(data.message || t('loginFailed'));
      }
    } catch {
      setError(t('networkError'));
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background: 'radial-gradient(1200px 600px at 80% -10%, #eef4ff 0%, #dbe6f8 60%, #cdddf3 100%)',
      }}
    >
      {/* Split card */}
      <div
        className="flex overflow-hidden"
        style={{
          width: 'min(1040px, 92vw)',
          height: 'min(620px, 88vh)',
          borderRadius: 18,
          boxShadow: '0 30px 80px rgba(20, 40, 90, 0.22)',
        }}
      >
        {/* Left: login form */}
        <div className="flex-1 bg-white flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-gray-900">{t('title')}</h1>
              {authMode === 'sso' && (
                <p className="text-sm text-gray-500 mt-1">{t('ssoSubtitle')}</p>
              )}
              {authMode === 'dev' && (
                <p className="text-sm text-gray-500 mt-1">{t('devModeNotice')}</p>
              )}
            </div>

            {/* SSO Login Form */}
            {authMode === 'sso' && !showDevForm && (
              <form onSubmit={handleSSOLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-4"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('password')}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-4"
                    placeholder="--------"
                  />
                </div>
                {error && <p className="text-sm text-error-5">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 px-4 bg-brand-5 text-white rounded-md hover:bg-brand-6 disabled:opacity-50 transition-colors"
                >
                  {loading ? t('signingIn') : t('ssoSignIn')}
                </button>
              </form>
            )}

            {/* Dev Token Login Form */}
            {(authMode === 'dev' || showDevForm) && (
              <form onSubmit={handleDevLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('devToken')}</label>
                  <input
                    type="password"
                    value={devToken}
                    onChange={(e) => setDevToken(e.target.value)}
                    required
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-4"
                    placeholder="--------"
                  />
                </div>
                {error && <p className="text-sm text-error-5">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 px-4 bg-brand-5 text-white rounded-md hover:bg-brand-6 disabled:opacity-50 transition-colors"
                >
                  {loading ? t('signingIn') : t('devSignIn')}
                </button>
              </form>
            )}

          </div>
        </div>

        {/* Right: star-network brand panel */}
        <div
          className="relative flex flex-col items-center justify-center overflow-hidden"
          style={{
            flex: '0 0 40%',
            background: 'linear-gradient(160deg, #0a1b3d 0%, #102a5c 55%, #16367a 100%)',
          }}
        >
          <StarNetwork />
          {/* Radial glow */}
          <div
            className="absolute pointer-events-none"
            style={{
              width: 420,
              height: 420,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(91,141,239,0.3), transparent 65%)',
              top: -120,
              right: -120,
              filter: 'blur(6px)',
            }}
          />
          {/* Brand text */}
          <div className="relative z-10 text-center px-7">
            <Image
              src={`${basePath}/logo.png`}
              alt="Gorilla Meet"
              width={84}
              height={84}
              className="rounded-full mx-auto"
              style={{ filter: 'drop-shadow(0 8px 24px rgba(43,92,255,0.45))' }}
            />
            <div
              className="mt-4 text-2xl font-extrabold tracking-wide"
              style={{
                background: 'linear-gradient(90deg, #cfe0ff, #ffffff)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Gorilla Meet
            </div>
            <div className="mt-2 text-sm font-medium tracking-wider" style={{ color: '#c7d6f5' }}>
              {t('tagline')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      <LoginForm />
    </Suspense>
  );
}
